import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import Loader from '__components/Kizen/Loader';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import useEndsOfScroll from 'hooks/useEndsOfScroll';
import { Icon } from '@kizen/kds/Icon';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import type { BillingGetAIUsageSummary } from '@kizen/api/billing';
import { useAreaColumns } from './columns';
import { getVisibleColumns } from './utils';
import {
  TableScrollArea,
  LoaderOverlay,
  ErrorOverlay,
  GRADIENT_Z_INDEX,
} from './styles';

const AreaTableScrollArea = styled(TableScrollArea)`
  min-height: 215px;
`;

type Props = {
  data?: BillingGetAIUsageSummary;
  loading: boolean;
  isError?: boolean;
};

export const UsageByAreaTable = ({ data, loading, isError }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const [, rightScrolled, , leftScrolled] = useEndsOfScroll(
    scrollRef,
    [data],
    1
  );

  const columns = useAreaColumns(
    useMemo(
      () =>
        data
          ? getVisibleColumns(data.columns, [
              data.agentic_workflows,
              data.system,
              data.employees,
              data.total,
            ])
          : [],
      [data]
    )
  );

  const rows = useMemo(
    () =>
      data
        ? [
            {
              label: t('AI Usage by Agentic Workflows'),
              usage: data.agentic_workflows,
            },
            { label: t('AI Features Usage by Platform'), usage: data.system },
            { label: t('AI Features Usage by Users'), usage: data.employees },
            { label: t('Total'), usage: data.total, isTotal: true },
          ]
        : [],
    [data, t]
  );

  return (
    <AreaTableScrollArea ref={scrollRef} className="kds-expanding-scrollbar">
      <Gradient
        position="left"
        fadeIn={!leftScrolled}
        zIndex={GRADIENT_Z_INDEX}
      />
      <BasicTable
        stickyHeader
        head={<TRow head columns={columns} />}
        data-qa-id="ai-usage-area-table"
        staleData={loading || isError}
      >
        {rows.map((row) => (
          <TRow key={row.label} columns={columns} data={row} />
        ))}
      </BasicTable>
      <Gradient
        position="right"
        fadeIn={!rightScrolled}
        zIndex={GRADIENT_Z_INDEX}
      />
      {isError ? (
        <ErrorOverlay data-qa-id="ai-usage-area-error">
          <Icon color="feedback-error" icon="message-info-circle" />
          <Spacer size={5} mode="vertical" />
          <Typography color="feedback-error">
            {t('Failed to load usage data for this cycle.')}
          </Typography>
        </ErrorOverlay>
      ) : loading ? (
        <LoaderOverlay>
          <Loader loading paddingTop={0} />
        </LoaderOverlay>
      ) : null}
    </AreaTableScrollArea>
  );
};
